import React, { useRef, useState, useCallback, useMemo } from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { useAuth } from '../../../../hooks/Auth';
import { useToast } from '../../../../hooks/Toast';
import { useLanguage } from '../../../../hooks/Language';
import getValidationErrors from '../../../../utils/getValidationErrors';
import getCep from '../../../../services/cep';

import Button from '../../../../components/Button';
import Loader from '../../../../components/Loader';

import { AnimatedDiv } from '../../styles';
import { LoginContent } from './styles';
import { authTitle } from '../../../../config';

import {
  REGISTRATION_FIELDS,
  FieldContext,
  buildSchema,
  normalizeData,
  isFieldVisible,
} from './registrationFields';

interface Props {
  /**
   * Ordem em que os campos aparecem e que são validados.
   * Ex: ['country', 'name', 'email', 'password', 'document_number', 'birth_date',
   *      'zipcode', 'address', 'address_number', 'neighborhood', 'city', 'state', 'term']
   */
  fields: string[];

  /**
   * Rótulos de seção opcionais. A chave é o `group` do campo.
   * Se você não passar, o form vira um bloco contínuo sem títulos.
   */
  groupLabels?: Record<string, string>;

  /** Dados iniciais do form. Padrão: { country: 'BR' }. */
  initialData?: Record<string, any>;

  /**
   * Callback com os dados já validados e normalizados. Se não passar,
   * o componente faz `api.post('/signup', data)` ou `PUT /update-user`
   * conforme `mode`.
   */
  onSubmit?: (data: Record<string, any>) => Promise<void> | void;

  /** 'signup' (padrão) faz POST /signup; 'update' faz PUT /update-user. */
  mode?: 'signup' | 'update';

  /** Dados arbitrários passados para os fields via ctx.extras. */
  extras?: Record<string, any>;

  /** JSX extra renderizado dentro do Form, antes do botão de submit (ex: reCAPTCHA). */
  beforeSubmit?: React.ReactNode;

  title?: string;
  subtitle?: string;
  submitLabel?: string;
}

const CadastroUnico: React.FC<Props> = ({
  fields,
  groupLabels,
  initialData = { country: 'BR' },
  onSubmit,
  mode = 'signup',
  extras,
  beforeSubmit,
  title,
  subtitle,
  submitLabel,
}) => {
  const formRef = useRef<FormHandles>(null);
  const { translate } = useLanguage();
  const { addToast } = useToast();
  const { user } = useAuth();

  const [countryISO, setCountryISO] = useState<string>(initialData.country ?? 'BR');
  const [loading, setLoading] = useState(false);

  /* ------------------------------------------------------------------
   * Estado reativo para condicionais (when).
   * Campos usados como "gatilho" devem chamar ctx.setValue no setCallback.
   * ------------------------------------------------------------------ */
  const [values, setValuesState] = useState<Record<string, any>>(initialData);
  const setValue = useCallback(
    (name: string, value: any) => setValuesState(prev => ({ ...prev, [name]: value })),
    [],
  );

  /* ------------------------------------------------------------------
   * CEP auto-preenchimento (só engata se 'zipcode' estiver nos fields)
   * ------------------------------------------------------------------ */
  const onCepFetch = useCallback(async (value: string) => {
    const res = await getCep(value);
    if (!res.error) {
      formRef.current?.setFieldValue('address', res.logradouro);
      formRef.current?.setFieldValue('neighborhood', res.bairro);
      formRef.current?.setFieldValue('city', res.localidade);
      formRef.current?.setFieldValue('state', res.uf);
    }
  }, []);

  const ctx: FieldContext = useMemo(
    () => ({
      countryISO,
      translate,
      formRef,
      onCepFetch,
      setCountryISO,
      user,
      values,
      setValue,
      extras,
    }),
    [countryISO, translate, onCepFetch, user, values, setValue, extras],
  );

  /* ------------------------------------------------------------------
   * Submit
   * ------------------------------------------------------------------ */
  const handleSubmit = useCallback(
    async (data: Record<string, any>) => {
      setLoading(true);
      try {
        formRef.current?.setErrors({});

        const schema = buildSchema(fields, ctx);
        await schema.validate(data, { abortEarly: false });

        const payload = normalizeData(data, fields, ctx);

        if (onSubmit) {
          await onSubmit(payload);
        } else {
          // Import lazy para evitar ciclo caso você não precise do default
          const api = (await import('../../../../services/api')).default;
          if (mode === 'update') {
            await api.put('/update-user', payload);
            window.location.reload();
          } else {
            await api.post('/signup', payload);
          }
        }
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          addToast({
            type: 'error',
            title: translate('Erro de validação'),
            description: err.errors.join(', '),
          });
        } else {
          addToast({
            type: 'error',
            title:
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              translate('Erro ao processar cadastro'),
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [fields, ctx, onSubmit, mode, addToast, translate],
  );

  /* ------------------------------------------------------------------
   * Renderização dos campos (agrupados ou em sequência)
   * ------------------------------------------------------------------ */
  const renderedFields = useMemo(() => {
    // Filtra campos escondidos por `when`
    const visible = fields.filter(n => isFieldVisible(n, ctx));

    if (!groupLabels) {
      return visible.map(name => {
        const def = REGISTRATION_FIELDS[name];
        if (!def) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(`[CadastroUnico] Campo desconhecido: "${name}"`);
          }
          return null;
        }
        return <React.Fragment key={name}>{def.render(ctx)}</React.Fragment>;
      });
    }

    // Agrupa preservando a ordem de aparição no array `fields`
    const order: string[] = [];
    const grouped: Record<string, string[]> = {};
    visible.forEach(name => {
      const def = REGISTRATION_FIELDS[name];
      const g = def?.group ?? 'default';
      if (!grouped[g]) {
        grouped[g] = [];
        order.push(g);
      }
      grouped[g].push(name);
    });

    return order.map(group => (
      <React.Fragment key={group}>
        {groupLabels[group] && (
          <h2
            style={{
              width: '100%',
              fontSize: 18,
              color: '#333',
              textAlign: 'center',
              margin: '30px auto 10px',
            }}
          >
            {groupLabels[group]}
          </h2>
        )}
        {grouped[group].map(n => {
          const def = REGISTRATION_FIELDS[n];
          if (!def) return null;
          return <React.Fragment key={n}>{def.render(ctx)}</React.Fragment>;
        })}
      </React.Fragment>
    ));
  }, [fields, groupLabels, ctx]);

  return (
    <AnimatedDiv visible={true}>
      <LoginContent>
        {loading && <Loader message="Carregando" />}
        <aside
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 30,
          }}
        >
          <h2 style={{ width: '100%', textAlign: 'center', color: '#333' }}>
            {title ?? translate('Novo cadastro')}
          </h2>
          {subtitle && (
            <p
              style={{
                width: '100%',
                textAlign: 'center',
                color: '#333',
                marginBottom: 20,
              }}
            >
              {subtitle}
            </p>
          )}

          <Form
            className="form"
            ref={formRef}
            onSubmit={handleSubmit}
            initialData={{country: 'BR'}}
          >
            {renderedFields}

            {fields.includes('term') && (
              <p
                style={{
                  padding: 10,
                  margin: '5px auto',
                  color: '#333',
                  fontSize: 12,
                  textAlign: 'left',
                }}
              >
                {translate('Ao me cadastrar, afirmo que li e aceito a')}{' '}
                <strong>
                  <Link style={{ display: 'inline', color: '#333' }} to="/termos" target="_BLANK">
                    {translate('POLÍTICA DE PRIVACIDADE')}
                  </Link>
                </strong>{' '}
                {translate('desta plataforma')} — {authTitle}
              </p>
            )}

            {beforeSubmit}

            <Button type="submit">
              {submitLabel ?? translate('Cadastrar')} <FiArrowRight />
            </Button>
          </Form>
        </aside>
      </LoginContent>
    </AnimatedDiv>
  );
};

export default CadastroUnico;
